import { Link } from "react-router-dom";
import { ArrowRight, Recycle, ScanLine, ShieldCheck, Snowflake } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { MaskedLines } from "@/components/MaskedLines";
import { Marquee } from "@/components/Marquee";
import { Seo } from "@/components/Seo";
import { useLang } from "@/i18n";

const VALUE_ICONS = [ScanLine, ShieldCheck, Recycle, Snowflake];

export default function About() {
  const { t } = useLang();
  return (
    <main>
      <Seo title={`${t("about.eyebrow")} | NordiGuard`} description={t("about.sub")} />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/hero-hiver.png" alt="" className="h-full w-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#05080E]/70 via-[#05080E]/85 to-[#05080E]" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 pt-40 pb-24 sm:px-8 lg:px-16">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sky-400">{t("about.eyebrow")}</p>
          <h1 className="font-display mt-4 max-w-4xl text-4xl font-extrabold leading-[1.05] tracking-tight text-slate-50 sm:text-5xl lg:text-6xl">
            <MaskedLines lines={[t("about.l1"), t("about.l2"), t("about.l3")]} />
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">{t("about.sub")}</p>
        </div>
      </section>

      <Marquee />

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-16 lg:py-28">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sky-400">{t("about.historyEyebrow")}</p>
          <h2 className="font-display mt-3 text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl lg:text-4xl">
            {t("about.historyTitle")}
          </h2>
        </Reveal>
        <div className="mt-14 grid gap-10 md:grid-cols-4">
          {t("about.timeline").map((tl, i) => (
            <Reveal key={tl.year} delay={i * 0.1}>
              <div data-testid={`timeline-step-${i}`} className="relative border-l border-sky-500/25 pl-6">
                <span className="absolute -left-[5px] top-1 h-2.5 w-2.5 rounded-full bg-sky-400 shadow-[0_0_12px_rgba(56,189,248,0.8)]" />
                <div className="font-mono-tech text-2xl font-semibold text-sky-400">{tl.year}</div>
                <h3 className="font-display mt-2 text-lg font-bold text-slate-50">{tl.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{tl.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-8 lg:px-16 lg:pb-28">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sky-400">{t("about.valuesEyebrow")}</p>
          <h2 className="font-display mt-3 text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl lg:text-4xl">
            {t("about.valuesTitle")}
          </h2>
        </Reveal>
        <div className="mt-12 grid gap-6 sm:grid-cols-2">
          {t("about.values").map((v, i) => {
            const Icon = VALUE_ICONS[i];
            return (
              <Reveal key={v.title} delay={i * 0.07}>
                <div data-testid={`value-card-${i}`} className="glacial-card flex h-full gap-5 p-7">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-sky-500/25 bg-sky-500/10 text-sky-400">
                    <Icon size={22} />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-bold text-slate-50">{v.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-400">{v.text}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal className="mt-16">
          <div className="glacial-card relative overflow-hidden p-8 sm:p-12">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(56,189,248,0.12),transparent_60%)]" />
            <div className="relative flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-center">
              <div className="max-w-2xl">
                <h2 className="font-display text-2xl font-bold text-slate-50 sm:text-3xl">{t("about.durTitle")}</h2>
                <p className="mt-3 text-sm leading-relaxed text-slate-400 sm:text-base">{t("about.durText")}</p>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">{t("about.durText2")}</p>
              </div>
              <img
                src="/images/couches-3.png"
                alt="Trois couches du tapis NordiGuard"
                loading="lazy"
                className="w-full max-w-sm rounded-xl border border-sky-500/15 object-cover shadow-2xl shadow-sky-950/50"
              />
              <Link
                to="/produits"
                data-testid="about-cta-models"
                className="group inline-flex shrink-0 items-center gap-2.5 rounded-full bg-sky-400 px-7 py-3.5 text-sm font-bold text-slate-950 transition-all duration-300 hover:bg-sky-300 hover:shadow-[0_0_30px_rgba(56,189,248,0.5)]"
              >
                {t("about.cta")}
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
